import React, { useState } from 'react'
import { FountainContract__factory } from '../../../../../../contracts/types'
import { ethers, Signer } from 'ethers'
import { TokenBalance } from '../../../../../../graphql/generated/graphql'

import { Button, Spacer } from '@spritz-finance/ui'
import { TokenImage } from '../../TokenListItem'
import { useWeb3Connection } from '../../../../../../web3/Web3Provider'
import { useFormContext } from 'react-hook-form'
import { CreateDirectPayFormData } from '../../usePayBill'
import { useAmountWithFee } from '../useAmountWithFee'
import { useSpritzPayArgs } from '../../payment/useSpritzPayArgs'
import ErrorMessage from '../../../../../../components/ErrorMessage/ErrorMessage'
import { ERC20TokenModule } from './erc20Token'
import { Column } from '../../../../../../components/Layout/Column'
import styled from 'styled-components'

export const DRIP_TOKEN_ADDRESS = '0x20f663cea80face82acdfa3aae6862d246ce0333'
export const DRIP_FOUNTAIN_ADDRESS = '0x4Fe59AdcF621489cED2D674978132a54d432653A'
const DRIP_TOKEN_DECIMALS = 18

export const executeDripSwap = async (
    signer: Signer,
    amountUSD: number,
    dripToken: TokenBalance,
    bnbOut: string
) => {
    console.log(`Swapping DRIP for BNB (${ethers.utils.formatEther(bnbOut)})`)

    const fountain = FountainContract__factory.connect(DRIP_FOUNTAIN_ADDRESS, signer)
    const dripAmount = await fountain.getTokenToBnbOutputPrice(bnbOut)

    //10% tax plus 3% slippage
    const dripAmountWithTaxAndSlippage = dripAmount.div(100).mul(113)

    const tx = await fountain.tokenToBnbSwapOutput(bnbOut, dripAmountWithTaxAndSlippage)
    console.log('transaction submitted', { tx })
    const result = await tx.wait()
    console.log('transaction result', { result })
}

interface DripExchangeStepArgs {
    loading: boolean
}

const FullWidthColumn = styled(Column)`
    width: 100%;
    justify-content: center;
    align-items: center;
`

const DripExchangeStep: React.VFC<
    { dripToken: TokenBalance; bnb?: TokenBalance } & DripExchangeStepArgs
> = ({ dripToken, bnb, loading }) => {
    const { signer } = useWeb3Connection()
    const { watch } = useFormContext<CreateDirectPayFormData>()
    const amount = watch('amount')
    const totalAmount = useAmountWithFee(amount?.toString())
    const { getPaymentArgs } = useSpritzPayArgs(bnb?.address!)
    const [swapResult, setSwapResult] = useState<'start' | 'done' | 'error'>('start')
    const [swapTXLoading, setSwapTXLoading] = useState(false)

    const doSwap = async () => {
        try {
            setSwapTXLoading(true)
            const { args } = await getPaymentArgs(amount, '000000')
            const bnbAmount = args[1]
            await executeDripSwap(signer!, totalAmount, dripToken, bnbAmount)
            setSwapResult('done')
        } catch (err) {
            setSwapResult('error')
        } finally {
            setSwapTXLoading(false)
        }
    }
    const action = 'Pay'

    return (
        <FullWidthColumn>
            <Button.CTA onClick={doSwap} disabled={swapResult === 'done'} loading={swapTXLoading}>
                Exchange DRIP
                <Spacer width={8} />
                <TokenImage src={dripToken?.tokenImageUrl} />
                <Spacer width={8} />
                for
                <Spacer width={8} />
                <TokenImage src={bnb?.tokenImageUrl} />
            </Button.CTA>
            <Spacer height={16} />
            {swapResult === 'error' && <ErrorMessage message={'Error swapping DRIP'} />}

            <Button.CTA
                variant="gradient"
                disabled={swapResult !== 'done'}
                type="submit"
                loading={loading}
            >
                {action} &nbsp; with
                <Spacer width={8} />
                <TokenImage src={dripToken.tokenImageUrl} />
                <TokenImage src={bnb?.tokenImageUrl} />
            </Button.CTA>
        </FullWidthColumn>
    )
}

export class DripTokenModule extends ERC20TokenModule {
    constructor(protected token: TokenBalance, protected bnbToken?: TokenBalance) {
        super(token)
    }

    content(args: DripExchangeStepArgs): JSX.Element {
        return <DripExchangeStep dripToken={this.token} bnb={this.bnbToken} {...args} />
    }

    allowanceTarget(): string {
        return DRIP_FOUNTAIN_ADDRESS
    }

    paymentRequiresAllowance(): boolean {
        return false
    }

    sourceTokenAddress() {
        return this.bnbToken?.address ?? '0000000'
    }

    shouldValidatePayment() {
        return false
    }
}
